import { ReactNode, useState } from "react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { RouterTextLink } from "components/actions/RouterLink";
import { DOTTED_UNDERLINE } from "components/contacts/contact_channels/ContactChannelList";
import Tooltip from "components/ui/tooltip/Tooltip";
import { appRoutes } from "lib/appRoutes";
import UIcon from "lib/icons/untitled_ui/UIcon";

/** The buyer fields both card flavours render; `Organisation` supplies all of them. */
export type LeadContactCardBuyer = {
  id: string;
  name: string;
  categoryAssignments?:
    | { categoryName: string; parentCategoryName?: string | null }[]
    | null
    | undefined;
} | null;

/**
 * A contact on the lead, as a collapsible card: name and role in the header with an actions slot,
 * expanding to its attributes alongside its contact details.
 *
 * Saved and suggested contacts share this so the two stay visually identical — a dashed border and
 * the "why suggested" explainer are the only differences. Anything card-shaped belongs here rather
 * than in either caller.
 */
export function LeadContactCard({
  variant,
  ariaLabel,
  name,
  jobTitle,
  seniority,
  jobFunction,
  buyer,
  explainer,
  explainerAriaLabel,
  actions,
  details,
  onOpen,
}: {
  variant: "saved" | "suggested";
  ariaLabel: string;
  name: string;
  jobTitle?: string | null;
  seniority: string;
  jobFunction: string;
  buyer: LeadContactCardBuyer;
  /** Tooltip shown when hovering the header — the suggestion explainer. */
  explainer?: ReactNode;
  explainerAriaLabel?: string;
  /** Header-right slot: accept/dismiss, or an actions menu. */
  actions: ReactNode;
  /** Right-hand column of the expanded panel — the contact's channels (see ContactChannelList). */
  details: ReactNode;
  /** Called when the card is expanded, for details that are fetched on demand. */
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const panelId = `lead-contact-panel-${ariaLabel}-${name}`;
  const buyerType = buyerTypeLabel(buyer);

  const toggleOpen = () => {
    const opening = !open;
    setOpen(opening);
    if (opening) onOpen?.();
  };

  const toggle = (
    <ToggleButton type="button" aria-expanded={open} aria-controls={panelId} onClick={toggleOpen}>
      <UIcon icon="user01" size={16} color="sysTextSecondary" />
      <Name>{name}</Name>
      {jobTitle && <JobTitle>{jobTitle}</JobTitle>}
      <Chevron isOpen={open} aria-hidden>
        <UIcon icon="chevronDown" size={16} color="sysTextSecondary" />
      </Chevron>
    </ToggleButton>
  );

  return (
    <Card aria-label={ariaLabel} variant={variant}>
      <HeaderRow>
        {explainer ? (
          // `aria-label` carries the whole explainer as one string — without it Radix duplicates the
          // visible text into a hidden node, and the heading alone wouldn't be announced.
          <Tooltip
            content={explainer}
            aria-label={explainerAriaLabel}
            side="top"
            variant="light"
            contentMaxWidth={320}
            delayDuration={300}
          >
            {toggle}
          </Tooltip>
        ) : (
          toggle
        )}
        <Actions>{actions}</Actions>
      </HeaderRow>

      {open && (
        <Panel id={panelId}>
          <Flex flexWrap="wrap" gap={24}>
            <Column>
              <DetailRow label="Seniority">{seniority}</DetailRow>
              <DetailRow label="Function">{jobFunction}</DetailRow>
              {buyer && (
                <>
                  <DetailRow label="Buyer">
                    <RouterTextLink {...appRoutes.buyerDetail(buyer.id)}>
                      {buyer.name}
                    </RouterTextLink>
                  </DetailRow>
                  {buyerType && <DetailRow label="Buyer type">{buyerType}</DetailRow>}
                </>
              )}
            </Column>
            <Column>{details}</Column>
          </Flex>
        </Panel>
      )}
    </Card>
  );
}

/** One `label: value` line in the expanded panel. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <DetailRowWrapper>
      <DetailLabel>{label}</DetailLabel>
      <DetailValue>{children}</DetailValue>
    </DetailRowWrapper>
  );
}

// Buyer type == the buyer's public-sector classification: the parent of each category assignment
// (e.g. "Central Government"). Falls back to the leaf category name when a parent is absent.
export function buyerTypeLabel(buyer: LeadContactCardBuyer): string | null {
  const assignments = buyer?.categoryAssignments ?? [];
  const parents = [
    ...new Set(assignments.map((c) => c.parentCategoryName).filter((n): n is string => Boolean(n))),
  ];
  if (parents.length) return parents.join(", ");
  const names = [...new Set(assignments.map((c) => c.categoryName).filter(Boolean))];
  return names.length ? names.join(", ") : null;
}

const Card = styled(Flex)<{ variant: "saved" | "suggested" }>(({ theme, variant }) => ({
  flexDirection: "column",
  borderRadius: 8,
  // Dashed marks a suggestion — something to act on rather than something already on the lead.
  border: `1px ${variant === "suggested" ? "dashed" : "solid"} ${theme.colors.sysBorderDecorative}`,
  background: theme.colors.sysBackgroundDefault,
  overflow: "hidden",
}));

const Chevron = styled.span<{ isOpen: boolean }>(({ isOpen }) => ({
  display: "inline-flex",
  flexShrink: 0,
  opacity: isOpen ? 1 : 0,
  transition: "opacity 0.1s ease, transform 0.1s ease",
  transform: isOpen ? "rotate(180deg)" : "none",
}));

const HeaderRow = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  transition: "background 0.1s ease",
  "&:hover": { background: theme.colors.sysBackgroundAlternative },
  [`&:hover ${Chevron}, &:focus-within ${Chevron}`]: { opacity: 1 },
}));

const ToggleButton = styled.button(({ theme }) => ({
  all: "unset",
  boxSizing: "border-box",
  flex: 1,
  minWidth: 0,
  minHeight: 56,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 12px 12px 16px",
  cursor: "pointer",
  // `all: unset` strips the default focus ring, so restore a visible one for keyboard users.
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysBorderDecorative}`,
    outlineOffset: -2,
  },
}));

const Name = styled.span(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.colors.sysTextDefault,
  whiteSpace: "nowrap",
}));

const JobTitle = styled.span(({ theme }) => ({
  // Shrinks (to an ellipsis) rather than growing, so the chevron sits next to the title itself.
  flex: "0 1 auto",
  minWidth: 0,
  fontSize: 14,
  color: theme.colors.sysTextSecondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Actions = styled(Flex)({
  alignItems: "center",
  gap: 4,
  flexShrink: 0,
  paddingRight: 12,
});

const Panel = styled(Flex)(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  padding: "14px 16px 16px 44px",
  flexDirection: "column",
  gap: 16,
  // One size for everything in the panel, the contact channels opposite included.
  fontSize: 14,
}));

const Column = styled(Flex)({
  flex: 1,
  minWidth: 200,
  flexDirection: "column",
  gap: 6,
});

const DetailRowWrapper = styled(Flex)({ gap: 8, alignItems: "flex-start" });

// Both columns of the panel read as one block: same size and weight throughout, with the label
// column quieter than what it labels. A value here and a phone number opposite share a colour,
// so the two stacks sit on one visual level.
const DetailLabel = styled.span(({ theme }) => ({
  width: 84,
  flexShrink: 0,
  fontSize: 14,
  lineHeight: 1.7,
  color: theme.colors.sysTextSecondary,
}));

const DetailValue = styled.span(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  lineHeight: 1.7,
  fontWeight: 500,
  color: theme.colors.sysTextDefault,
  // A link in a detail value reads as the value it is, not as a link: the same quiet dotted
  // underline the email and LinkedIn channels opposite use, in the value's own colour. The
  // shared link component paints itself link-blue from a single class, and underlines solid on
  // hover, so both rules have to outrank it — hence `a:hover` rather than just `a`.
  a: { ...DOTTED_UNDERLINE, color: theme.colors.sysTextDefault },
  "a:hover": { ...DOTTED_UNDERLINE, color: theme.colors.sysTextDefault },
}));

/** Muted line for "no details" / "revealed when you add" copy in the details column. Sized with
 * the rest of the panel, but kept a shade quieter: it is an absence, not a value. */
export const ContactHint = styled.span(({ theme }) => ({
  marginTop: 2,
  fontSize: 14,
  color: theme.colors.sysTextInactive,
}));
